import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import type { PublicChatMessage } from "../api/publicChat";

const TOP_OFFSET_PX = 16;
const BOTTOM_OFFSET_PX = 24;
const AT_BOTTOM_THRESHOLD_PX = 80;
// Window after a programmatic scroll during which onScroll events are ignored,
// so our own scrolls aren't mistaken for the user scrolling.
const PROGRAMMATIC_WINDOW_MS = 300;

interface Args {
  /** Number of settled messages (excludes the in-flight streaming bubble). */
  messageCount: number;
  /** Role of the last settled message, or undefined when there are none. */
  lastMessageRole: PublicChatMessage["role"] | undefined;
  isStreaming: boolean;
  streamingContent: string;
}

/**
 * Mirrors the in-app Jan chat scroll behavior (ChatMessageList) for the public
 * embed: when a new user question arrives it is anchored to the TOP of the
 * scroll viewport (with a bottom spacer giving the room to do so), the response
 * then streams into the space below WITHOUT auto-scrolling, and a floating
 * "scroll to bottom" button appears while there's unseen content below.
 *
 * Embed-specific vs. the in-app version:
 * - Scrolls by adjusting the widget's own container `scrollTop` (never
 *   `scrollIntoView`), so focusing/anchoring can't scroll the host WordPress
 *   page.
 * - The spacer is sized to the scroll container's height (not `100vh`) so a
 *   short embed doesn't get a viewport-tall stretch of dead space.
 */
export const usePublicChatScroll = ({
  messageCount,
  lastMessageRole,
  isStreaming,
  streamingContent,
}: Args) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const lastUserMessageRef = useRef<HTMLDivElement>(null);
  // 0-height marker rendered at the end of the streamed content (before the
  // spacer) so "scroll to bottom" lands on the answer, not the empty spacer.
  const streamingEndRef = useRef<HTMLDivElement>(null);
  const prevCountRef = useRef(0);
  const programmaticTsRef = useRef(0);
  const [spacerHeight, setSpacerHeight] = useState(0);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);

  const markProgrammatic = useCallback(() => {
    programmaticTsRef.current = Date.now();
  }, []);

  // Keep the spacer as tall as the scroll viewport so the latest question can
  // always reach the top, and re-measure when the embed is resized.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const measure = () => setSpacerHeight(el.clientHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const scrollQuestionToTop = useCallback(() => {
    const el = lastUserMessageRef.current;
    const container = scrollRef.current;
    if (!el || !container) return;
    markProgrammatic();
    const containerRect = container.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    container.scrollTop += elRect.top - containerRect.top - TOP_OFFSET_PX;
  }, [markProgrammatic]);

  // The button shows when the end-of-content marker is below the visible area,
  // i.e. there's streamed text the user hasn't scrolled to yet.
  const updateButton = useCallback(() => {
    const container = scrollRef.current;
    const anchor = streamingEndRef.current;
    if (!container || !anchor) {
      setShowScrollToBottom(false);
      return;
    }
    const containerRect = container.getBoundingClientRect();
    const anchorRect = anchor.getBoundingClientRect();
    setShowScrollToBottom(
      anchorRect.bottom > containerRect.bottom + AT_BOTTOM_THRESHOLD_PX
    );
  }, []);

  const onScroll = useCallback(() => {
    if (Date.now() - programmaticTsRef.current < PROGRAMMATIC_WINDOW_MS) return;
    updateButton();
  }, [updateButton]);

  const scrollToBottom = useCallback(() => {
    const container = scrollRef.current;
    const anchor = streamingEndRef.current;
    if (!container) return;
    markProgrammatic();
    if (anchor) {
      const containerRect = container.getBoundingClientRect();
      const anchorRect = anchor.getBoundingClientRect();
      container.scrollTop +=
        anchorRect.bottom - containerRect.bottom + BOTTOM_OFFSET_PX;
    } else {
      container.scrollTop = container.scrollHeight;
    }
    setShowScrollToBottom(false);
  }, [markProgrammatic]);

  // Anchor a new user question to the top. useLayoutEffect (pre-paint) so an SSE
  // chunk arriving immediately after can't fight the scroll position.
  useLayoutEffect(() => {
    if (messageCount > prevCountRef.current && lastMessageRole === "user") {
      const container = scrollRef.current;
      // Guarantee scroll room exists before the first anchor if the
      // ResizeObserver hasn't measured yet.
      if (container && spacerHeight === 0)
        setSpacerHeight(container.clientHeight);
      scrollQuestionToTop();
      // Re-apply after paint in case layout (spacer height) wasn't settled yet.
      requestAnimationFrame(() => scrollQuestionToTop());
    }
    prevCountRef.current = messageCount;
  }, [messageCount, lastMessageRole, scrollQuestionToTop, spacerHeight]);

  // During streaming: never auto-scroll — only refresh the button so the user
  // reads at their own pace.
  useEffect(() => {
    if (!isStreaming && !streamingContent) return;
    const raf = requestAnimationFrame(updateButton);
    return () => cancelAnimationFrame(raf);
  }, [isStreaming, streamingContent, updateButton]);

  const showSpacer = isStreaming || lastMessageRole === "user";

  return {
    scrollRef,
    lastUserMessageRef,
    streamingEndRef,
    /** Px height for the bottom spacer; 0 when no spacer should render. */
    spacerHeight: showSpacer ? spacerHeight : 0,
    showSpacer,
    showScrollToBottom,
    scrollToBottom,
    onScroll,
  };
};
